// src/components/js/registroSAC.js
import React, { useState, useEffect, forwardRef } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ptBR } from 'date-fns/locale';
import { useLocation, useNavigate } from 'react-router-dom';
import SacService, { tiposOcorrencia } from '../../modules/services/SacService';
import { format, parse, isValid } from 'date-fns';
import { FaInfoCircle } from "react-icons/fa";
import { GoChevronUp, GoChevronDown } from "react-icons/go";
import { FaChevronLeft, FaChevronRight, FaCamera, FaTrash, FaFilePdf } from "react-icons/fa";
import IaService from '../../modules/services/IaService'; // <--- IMPORTAR O SERVIÇO
import { FaRobot } from "react-icons/fa"; // <--- Ícone para o robô (instale se não tiver ou use outro)
import '../css/boletos.css';

// === FEATURE FLAG PARA UPLOAD DE IMAGENS ===
const HABILITAR_UPLOAD = true; // Mude para true quando quiser ativar
// ===========================================

// Componente de input customizado para o DatePicker
const CustomDateInput = forwardRef(({ value, onClick, onChange, placeholder, onBlur }, ref) => (
  <input
    type="text"
    name="dtEmissao"
    placeholder={placeholder}
    value={value}
    onClick={onClick}
    onChange={onChange}
    onBlur={onBlur}
    maxLength="10"
    className="input-filtro"
    ref={ref}
  />
));

function RegistroSAC({ cliente }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Sem state (ex.: redirecionamento direto), default = Devolução; pelo menu SAC o state define o motivo
  const tipoOcorrenciaInicial = location.state?.tipoOcorrencia || 'DEVOLUCAO';
  const [imagens, setImagens] = useState([]);

  const [dadosFormulario, setDadosFormulario] = useState({
    nrNfe: '',
    dtEmissao: null,
    itens: [],
    itensSobra: [],
    nomeSolicitante: '',
    emailSolicitante: '',
    telefoneSolicitante: '',
    observacao: '',
    tipoOcorrencia: tipoOcorrenciaInicial, 
    cdEmpresaNota: '' 
  });

  // STATES IA
  const [analisandoIA, setAnalisandoIA] = useState(false);
  const [sugestaoIA, setSugestaoIA] = useState(null);
  const [jaVerificouIA, setJaVerificouIA] = useState(false); // NOVO: Controla se o usuário já viu o aviso

  // Se o usuário mudar a observação, resetamos a verificação para analisar de novo
  useEffect(() => {
    if (jaVerificouIA) setJaVerificouIA(false);
    if (sugestaoIA) setSugestaoIA(null);
  }, [dadosFormulario.observacao]);
  
  // Mapeamento de códigos para o "Caminho" visual do menu
  const MAPA_CAMINHOS = {
    'DEVOLUCAO': 'SAC -> Registrar Devolução',
    'SOBRA_MERCADORIA': 'SAC -> Registrar Falta e/ou Sobra',
    'FALTA_MERCADORIA': 'SAC -> Registrar Falta e/ou Sobra',
    'SOLICITACAO': 'SAC -> Registrar Solicitação'
  };

  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [valorDataTexto, setValorDataTexto] = useState('');
  const [mostrarSelecaoTodos, setMostrarSelecaoTodos] = useState(false);
  const [submotivoTodos, setSubmotivoTodos] = useState(null);
  const [informacaoCorretaTodos, setInformacaoCorretaTodos] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(50);
  const [loadingEAN, setLoadingEAN] = useState({});

  useEffect(() => {
    if (tipoOcorrenciaInicial) {
      setDadosFormulario({
        nrNfe: '',
        dtEmissao: null,
        itens: [],
        itensSobra: [], // Importante zerar isso também se trocar de aba
        nomeSolicitante: '',      // Zerado conforme solicitado
        emailSolicitante: '',     // Zerado conforme solicitado
        telefoneSolicitante: '',  // Zerado conforme solicitado
        observacao: '',           // Zerado conforme solicitado
        tipoOcorrencia: tipoOcorrenciaInicial,
        cdEmpresaNota: '',
      });

      setImagens([]);
      setValorDataTexto('');
      
      // Recomendo forçar o reset dos estados da IA também para evitar bugs visuais
      setSugestaoIA(null);
      setJaVerificouIA(false);
    }
  }, [tipoOcorrenciaInicial]);

  // --- LÓGICA DE UPLOAD (AVARIA) ---
  const temItemAvariaQualificado = () => {
    // Se o recurso estiver desligado, retorna false imediatamente
    if (!HABILITAR_UPLOAD) return false;

    // Verifica se algum item selecionado tem motivo 2/27 OU 7/54
    return dadosFormulario.itens.some(item => {
      const sub = item.submotivoSelecionado;
      if (!sub) return false;
      return (sub.cdTipo === 2 && sub.cdSubtipo === 27) || 
             (sub.cdTipo === 7 && sub.cdSubtipo === 54);
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (imagens.length + files.length > 2) {
      alert("Máximo de 2 imagens permitido.");
      return;
    }

    const validFiles = [];
    for (let file of files) {
      if (file.size > 1 * 1024 * 1024) { // 2MB
        alert(`A imagem ${file.name} excede o limite de 1 MB.`);
        continue;
      }
      if (!file.type.startsWith('image/')) {
        alert(`O arquivo ${file.name} não é uma imagem válida.`);
        continue;
      }
      validFiles.push(file);
    }
    setImagens(prev => [...prev, ...validFiles]);
    // Limpa o input para permitir selecionar a mesma foto se ela foi removida
    e.target.value = '';
  };

  const removerImagem = (index) => {
    setImagens(prev => prev.filter((_, i) => i !== index));
  };
  // ---------------------------------

  const getTipoOcorrenciaDisplay = (tipo) => {
    switch (tipo) {
        case 'SOBRA_MERCADORIA':
            return 'Falta e/ou Sobra de Mercadoria';
        case 'DEVOLUCAO':
            return 'Devolução';
        case 'SOLICITACAO':
            return 'Solicitação';
        default:
            return tipo;
    }
  };

  const getResumoOcorrencia = (tipo) => {
      switch (tipo) {
          case 'DEVOLUCAO':
              return 'Motivos: Mercadoria errada, Avaria, Atraso, Defeito de Fábrica, Diferença de Preço, Erro de Digitação, Temperatura, Recall ou Vencimento Curto.';
          case 'SOBRA_MERCADORIA':
              return 'Motivos: Falta de mercadoria, mercadoria enviada em excesso ou mercadoria que não pertence a esta nota fiscal.';
          case 'SOLICITACAO':
              return 'Motivos: Cartas de correção (Lote, Validade, Nota).';
          default:
              return '';
      }
  };

  // Função para verificar se existe algum item com submotivo "Outro"
  const temSubmotivoOutro = () => {
    return dadosFormulario.itens.some(item => 
      item.motivoSelecionado && 
      item.submotivoSelecionado && 
      (item.submotivoSelecionado.label === 'OUTROS' || item.submotivoSelecionado.value === 'OUTRO')
    );
  };

  const consultarEAN = async (ean, index) => {
    if (!ean || ean.length < 4) return;
    
    setLoadingEAN(prev => ({ ...prev, [index]: true }));
    
    // Atualiza para mostrar "Aguarde, carregando..."
    setDadosFormulario(prev => {
        const novosItensSobra = [...(prev.itensSobra || [])];
        novosItensSobra[index] = {
            ...novosItensSobra[index],
            ean: ean,
            descricao: 'Aguarde, carregando...',
            cdMercadoriaEncontrada: null,
            eanValido: null,
            erroEAN: null // Adicionamos campo de erro dinâmico
        };
        return { ...prev, itensSobra: novosItensSobra };
    });

    try {
        console.log('🔍 Consultando EAN:', ean);
        const response = await SacService.consultarMercadoriaPorEAN(ean);
        
        if (response.status === 'OK' && response.conteudo) {
            const mercadoria = response.conteudo;
            
            setDadosFormulario(prev => {
                const novosItensSobra = [...(prev.itensSobra || [])];
                const codigoEncontrado = mercadoria.cdMercadoria;

                // === NOVA LÓGICA DE VALIDAÇÃO DE DUPLICIDADE EM TEMPO REAL ===
                // Verifica se o produto já está selecionado na grade de cima
                const jaNaNota = prev.itens.some(i => !!i.motivoSelecionado && Number(i.codMercadoria) === Number(codigoEncontrado));
                // Verifica se o produto já foi adicionado em outra linha da sobra
                const jaNaSobra = prev.itensSobra.some((i, idx) => idx !== index && Number(i.cdMercadoriaEncontrada) === Number(codigoEncontrado));

                if (jaNaNota || jaNaSobra) {
                    novosItensSobra[index] = {
                        ...novosItensSobra[index],
                        ean: ean,
                        descricao: '',
                        cdMercadoriaEncontrada: null,
                        eanValido: false,
                        erroEAN: jaNaNota ? '⚠️ Produto já marcado na nota fiscal acima.' : '⚠️ Este produto já foi adicionado nas sobras.'
                    };
                } else {
                    novosItensSobra[index] = {
                        ...novosItensSobra[index],
                        ean: ean,
                        descricao: mercadoria.descricao || mercadoria.nmMercadoria || '',
                        cdMercadoriaEncontrada: codigoEncontrado,
                        eanValido: true,
                        erroEAN: null
                    };
                }
                // ==============================================================

                return { ...prev, itensSobra: novosItensSobra };
            });
            
            console.log('✅ Mercadoria encontrada:', mercadoria);
        } else {
            console.log('⚠️ EAN não encontrado:', ean);
            
            setDadosFormulario(prev => {
                const novosItensSobra = [...(prev.itensSobra || [])];
                novosItensSobra[index] = {
                    ...novosItensSobra[index],
                    ean: ean,
                    descricao: '',
                    cdMercadoriaEncontrada: null,
                    eanValido: false,
                    erroEAN: 'EAN não encontrado no sistema'
                };
                return { ...prev, itensSobra: novosItensSobra };
            });
        }
    } catch (error) {
        console.error('❌ Erro ao consultar EAN:', error);
        
        setDadosFormulario(prev => {
            const novosItensSobra = [...(prev.itensSobra || [])];
            novosItensSobra[index] = {
                ...novosItensSobra[index],
                ean: ean,
                descricao: '',
                cdMercadoriaEncontrada: null,
                eanValido: false,
                erroEAN: 'Erro ao consultar servidor'
            };
            return { ...prev, itensSobra: novosItensSobra };
        });
    } finally {
        setLoadingEAN(prev => ({ ...prev, [index]: false }));
    }
  };
 
  // Termos que indicam que o usuário está tentando relatar produtos no campo errado
  const TERMOS_PRODUTO = ['sobrou', 'faltou', 'veio a mais', 'unidades', 'caixas', 'peças', 'mercadoria'];

  const detectarRelatoIncorreto = (texto) => {
      const textoMinusculo = texto.toLowerCase();
      return TERMOS_PRODUTO.some(termo => textoMinusculo.includes(termo));
  };
  
  // Função para formatar o telefone
  const formatarTelefone = (valor) => {
    // Remove todos os caracteres não numéricos
    const apenasNumeros = valor.replace(/\D/g, '');
    // Aplica a máscara baseada na quantidade de dígitos
    if (apenasNumeros.length <= 2) {
      return `(${apenasNumeros}`;
    } else if (apenasNumeros.length <= 7) {
      return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2)}`;
    } else if (apenasNumeros.length <= 10) {
      return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2, 6)}-${apenasNumeros.slice(6)}`;
    } else {
      return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2, 7)}-${apenasNumeros.slice(7, 11)}`;
    }
  };
  
  // Função para lidar com a mudança no input
  const handleTelefoneChange = (e) => {
    const valor = e.target.value;
    const telefoneFormatado = formatarTelefone(valor);
    // Atualiza o estado com o valor formatado
    setDadosFormulario(prev => ({
      ...prev,
      telefoneSolicitante: telefoneFormatado
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDadosFormulario(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setDadosFormulario(prev => ({ ...prev, dtEmissao: date }));
  };

  // --- NOVA FUNÇÃO: Verifica se o item é Defeito de Fábrica ---
  const isDefeitoFabrica = (item) => {
    const sub = item?.submotivoSelecionado;
    return (sub?.cdTipo === 6 && sub?.cdSubtipo === 1) || (sub?.cdTipo === 7 && sub?.cdSubtipo === 55);
  };

  const isRecall = (item) => {
    const sub = item?.submotivoSelecionado;
    return sub?.cdTipo === 7 && sub?.cdSubtipo === 59;
  };

  const isRessarcimento = (item) => {
      const sub = item?.submotivoSelecionado;
      return sub?.cdTipo === 1 && sub?.cdSubtipo === 35;
  };

  const isForaDaTemperatura = (item) => {
    const sub = item?.submotivoSelecionado;
    return sub?.cdTipo === 7 && sub?.cdSubtipo === 37;
  };

  // --- NOVAS FUNÇÕES PARA VALIDAÇÃO DO REEMBOLSO E OBSERVAÇÃO ---
  const temLocalDescontoOutros = () => {
    return dadosFormulario.itens.some(item => 
      item.motivoSelecionado && 
      isRessarcimento(item) && 
      item.localDesconto === 'OUTROS'
    );
  };

  const isObservacaoObrigatoria = temSubmotivoOutro() || temLocalDescontoOutros();
  // --------------------------------------------------------------

  const isCopiaMinuta = (item) => {
    const sub = item?.submotivoSelecionado;
    return sub?.cdTipo === 1 && sub?.cdSubtipo === 36;
};

  const isCorrecao = (item) => {
    const sub = item?.submotivoSelecionado;
    // Só mostra Informação Correta se for Solicitação E NÃO for reembolso E NÃO for cópia de minuta
    return dadosFormulario.tipoOcorrencia === 'SOLICITACAO' && !!sub && !isRessarcimento(item) && !isCopiaMinuta(item);
  };

  // --- VARIÁVEIS DE CONTROLE DE COLUNAS (SÓ APARECEM SE O ITEM ESTIVER MARCADO) ---
  const exibirColunaInformacaoCorreta = dadosFormulario.itens.some(item => item.motivoSelecionado && isCorrecao(item));
  const exibirColunaRessarcimento = dadosFormulario.itens.some(item => item.motivoSelecionado && isRessarcimento(item));
  const exibirColunaRecall = dadosFormulario.itens.some(item => item.motivoSelecionado && isRecall(item));
  const exibirColunaProtocolo = dadosFormulario.itens.some(item => item.motivoSelecionado && isDefeitoFabrica(item));
  const exibirColunaTemperatura = dadosFormulario.itens.some(item => item.motivoSelecionado && isForaDaTemperatura(item));

  const isRefrigerado = (item) => {
    return item.idRefrigerado === 'S'; 
  };

  const deveBloquearItem = (item) => {
      // Bloqueia se for Devolução E Refrigerado
      return dadosFormulario.tipoOcorrencia === 'DEVOLUCAO' && isRefrigerado(item);
  };

  // --- NOVO HANDLER: Atualiza o protocolo no estado ---
  const handleProtocoloIndustriaChange = (e, itemIndex) => {
    const indiceReal = (paginaAtual - 1) * itensPorPagina + itemIndex;
    const novosItens = [...dadosFormulario.itens];
    novosItens[indiceReal].protocoloIndustria = e.target.value;
    setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
  };
  // --- FIM DA NOVA FUNÇÃO DO DEFEITO DE FÁBRICA ---

  // Função para verificar se é tipo solicitação
  const temTipoSolicitacao = (itemIndex) => {
      const item = dadosFormulario.itens[itemIndex];
      return item?.submotivoSelecionado && 
          dadosFormulario.tipoOcorrencia === 'SOLICITACAO';
  };
  
  // Função para lidar com informação correta
  const handleInformacaoCorretaChange = (e, itemIndex) => {
      const novosItens = [...dadosFormulario.itens];
      novosItens[itemIndex].informacaoCorreta = e.target.value;
      setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
  };

  const adicionarItemSobra = () => {
    setDadosFormulario(prev => ({
      ...prev,
      itensSobra: [...(prev.itensSobra || []), { ean: '', descricao: '', quantidade: '', motivo: '', cdMercadoriaEncontrada: null, eanValido: null }]
    }));
  };
  
  const removerItemSobra = (index) => {
    setDadosFormulario(prev => ({
      ...prev,
      itensSobra: prev.itensSobra.filter((_, i) => i !== index)
    }));
  };
  
  const handleItemSobraChange = (index, campo, valor) => {
    // === [NOVO] RESET DA IA ===
    if (campo === 'motivo') {
      setSugestaoIA(null);
      setJaVerificouIA(false);
    }
    // ==========================
    
    setDadosFormulario(prev => {
      const novosItensSobra = [...(prev.itensSobra || [])];
      novosItensSobra[index] = { ...novosItensSobra[index], [campo]: valor };

      // Se alterou o EAN, limpa os dados de mercadoria encontrada
      if (campo === 'ean') {
        novosItensSobra[index].cdMercadoriaEncontrada = null;
        novosItensSobra[index].eanValido = null;
        if (!valor) {
          novosItensSobra[index].descricao = '';
        }
      }

      return { ...prev, itensSobra: novosItensSobra };
    });

    if (campo === 'ean' && valor && valor.length >= 8) {
      consultarEAN(valor, index);
    }
  };

  // Função para validar se o nome tem apenas letras e espaços (mínimo 2 caracteres)
  const isNomeValido = (nome) => {
    if (!nome) return false;
    // Permite letras (a-z), acentos (À-ÿ) e espaços (\s). Rejeita números, pontos e vírgulas.
    return /^[a-zA-ZÀ-ÿ\s]{2,}$/.test(nome.trim());
  };

  // Funções para lidar com a seleção dos itens da tabela
  const handleItemSelect = (index) => {
    const indiceReal = (paginaAtual - 1) * itensPorPagina + index;

    // ============================================================
    // VALIDAÇÃO DE BLOQUEIO (Adicione este bloco no início)
    // ============================================================
    const itemParaVerificar = dadosFormulario.itens[indiceReal];
    if (deveBloquearItem(itemParaVerificar)) {
        return; // Se for termolábil em devolução, não faz nada
    }
    // ============================================================

    setDadosFormulario(prev => {
      const novosItens = [...prev.itens];
      const item = novosItens[indiceReal];
      const isSelected = !!item.motivoSelecionado;
      
      if (isSelected) {
        item.motivoSelecionado = null;
        item.submotivoSelecionado = null;

        // Limpar campos extras se desmarcar
        item.protocoloIndustria = '';
      } else {
        // const tipoInicial = tiposOcorrencia[prev.tipoOcorrencia] ? { value: prev.tipoOcorrencia, label: prev.tipoOcorrencia } : null;
        // item.motivoSelecionado = tipoInicial;
        // item.submotivos = tiposOcorrencia[prev.tipoOcorrencia] || [];

        // Buscar o primeiro subtipo para pegar o cdTipo
        const subtipos = tiposOcorrencia[prev.tipoOcorrencia] || [];
        item.motivoSelecionado = { 
          value: prev.tipoOcorrencia, 
          label: prev.tipoOcorrencia,
          cdTipo: null // Garante que o cdTipo venha do submotivo selecionado
        };
        item.submotivoSelecionado = null;

      }
      
      return { ...prev, itens: novosItens };
    });
  };

  const handleSelectAllItems = () => {
    const todosSelecionados = dadosFormulario.itens.every(item => !!item.motivoSelecionado);
    
    if (todosSelecionados) {
      // Se todos estão selecionados, desselecionar todos
      setDadosFormulario(prev => {
        const novosItens = prev.itens.map(item => ({
          ...item,
          motivoSelecionado: null,
          submotivos: [],
          submotivoSelecionado: null
        }));
        return { ...prev, itens: novosItens };
      });
    } else {
      // Se nem todos estão selecionados, mostrar seleção de submotivo (mantém a lógica atual)
      setMostrarSelecaoTodos(true);
    }
  };

  const handleConfirmarSelecaoTodos = () => {
    if (!submotivoTodos) {
      alert('Por favor, selecione um motivo.');
      return;
    }
  
    setDadosFormulario(prev => {
      const novosItens = prev.itens.map(item => ({
        ...item,
        motivoSelecionado: {
          value: prev.tipoOcorrencia,
          label: prev.tipoOcorrencia,
          cdTipo: submotivoTodos?.cdTipo || (tiposOcorrencia[prev.tipoOcorrencia]?.[0]?.cdTipo)
        },
        submotivos: tiposOcorrencia[prev.tipoOcorrencia] || [],
        submotivoSelecionado: submotivoTodos,
        informacaoCorreta: dadosFormulario.tipoOcorrencia === 'SOLICITACAO' ? informacaoCorretaTodos : '' // APLICAR A INFORMAÇÃO CORRETA A TODOS
      }));
      return { ...prev, itens: novosItens };
    });
    setMostrarSelecaoTodos(false);
  };

  const handleCancelarSelecaoTodos = () => {
    setMostrarSelecaoTodos(false);
    setSubmotivoTodos(null);
    setInformacaoCorretaTodos('');
  };

  const handleSubmotivoChange = (selectedOption, itemIndex) => {
    const indiceReal = (paginaAtual - 1) * itensPorPagina + itemIndex;
    const novosItens = [...dadosFormulario.itens];
    novosItens[indiceReal].submotivoSelecionado = selectedOption;

    // Se mudou o motivo, limpa o protocolo caso não seja mais defeito de fábrica
    if (!isDefeitoFabrica(novosItens[indiceReal])) {
      novosItens[indiceReal].protocoloIndustria = '';
    }

    // === [NOVO] RESET DA IA AO MUDAR O MOTIVO ===
    setSugestaoIA(null);     // Remove a caixa amarela
    setJaVerificouIA(false); // Obriga a IA a validar de novo no próximo clique
    // ============================================

    setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
  };
  
  const handleQuantidadeDevolucao = (e, itemIndex) => {
    const indiceReal = (paginaAtual - 1) * itensPorPagina + itemIndex;
    const novosItens = [...dadosFormulario.itens];
    const novaQuantidade = parseInt(e.target.value, 10);
    if (novaQuantidade <= novosItens[indiceReal].quantidade) {
      novosItens[indiceReal].quantidadeDevolucao = novaQuantidade;
      setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
    } else {
      alert(`A ${getQuantidadeLabel(dadosFormulario.tipoOcorrencia).toLowerCase()} não pode ser maior que a quantidade original.`);
    }
  };

  const calcularItensExibidos = () => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return dadosFormulario.itens.slice(inicio, fim);
  };
  
  const calcularTotalPaginas = () => {
    return Math.ceil(dadosFormulario.itens.length / itensPorPagina);
  };
  
  const irParaPagina = (numeroPagina) => {
    setPaginaAtual(numeroPagina);
  };
  
  const paginaAnterior = () => {
    if (paginaAtual > 1) {
      setPaginaAtual(paginaAtual - 1);
    }
  };
  
  const proximaPagina = () => {
    const totalPaginas = calcularTotalPaginas();
    if (paginaAtual < totalPaginas) {
      setPaginaAtual(paginaAtual + 1);
    }
  };

  const handleConsultarNota = async () => {
    setIsLoading(true);
    setErro('');
    setDadosFormulario(prev => ({ ...prev, itens: [] }));
    setPaginaAtual(1);

    setMostrarSelecaoTodos(false);
    setSubmotivoTodos(null);
    
    if (!dadosFormulario.nrNfe || !dadosFormulario.dtEmissao) {
      setErro('Por favor, informe o número da NF e a data de emissão.');
      setIsLoading(false);
      return;
    }

    try {
      const dtEmissaoFormatada = dadosFormulario.dtEmissao ? format(dadosFormulario.dtEmissao, 'dd/MM/yyyy') : null;
      const response = await SacService.consultarNotaFiscal(cliente.cdCliente, dadosFormulario.nrNfe, dtEmissaoFormatada);
      
      if (response.status === 'OK' && response.conteudo) {
        const notaEncontrada = response.conteudo;
        if (notaEncontrada && notaEncontrada.itens) {
          const itensComOpcoes = notaEncontrada.itens.map(item => ({
            ...item,
            motivoSelecionado: null,
            submotivoSelecionado: null,
            submotivos: tiposOcorrencia[dadosFormulario.tipoOcorrencia] || [],
            quantidadeDevolucao: item.quantidade,
            informacaoCorreta: '',
            protocoloIndustria: '', // Inicializa campo
            localDesconto: '' // NOVO CAMPO: Inicializa local do desconto
          }));
          const formularioAtualizado = {
              ...dadosFormulario,
              itens: itensComOpcoes,
              cdEmpresaNota: notaEncontrada.cdEmpresa
            }; 
            console.log('Dados formulário atualizados:', formularioAtualizado); 
            setDadosFormulario(formularioAtualizado); 
        } else {
          setErro('Nenhum item encontrado para a nota fiscal informada.');
        }
      } else {
        setErro(response.mensagem);
      }
    } catch (err) {
      console.log(err)
      setErro('Por favor, tente novamente.');
      
      setDadosFormulario(prev => ({
        ...prev,
        nrNfe: '',       // Limpa o número da nota
        dtEmissao: null  // Limpa a data de emissão
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Função para obter o rótulo da coluna de quantidade
  const getQuantidadeLabel = (tipoOcorrencia) => {
    switch (tipoOcorrencia) {
      case 'DEVOLUCAO':
        return 'Qtd. Devolução';
      case 'SOBRA_MERCADORIA':
        return 'Qtd. Afetada';
      case 'SOLICITACAO':
        return 'Qtd. Solicitada';
      default:
        return 'Qtd. Devolução';
    }
  };

  // Função para obter o placeholder do input
  const getQuantidadePlaceholder = (tipoOcorrencia) => {
    switch (tipoOcorrencia) {
      case 'DEVOLUCAO':
        return 'Qtd a devolver';
      case 'SOBRA_MERCADORIA':
        return 'Qtd afetada';
      case 'SOLICITACAO':
        return 'Qtd solicitada';
      default:
        return 'Quantidade';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErro('');

    // =====================================================================
    // 1. ANÁLISE DE IA (SÓ RODA SE AINDA NÃO VERIFICOU)
    // =====================================================================
    if (!jaVerificouIA) {
      try {
          const itensAtivos = [
              ...dadosFormulario.itens.filter(i => !!i.motivoSelecionado),
              ...(dadosFormulario.itensSobra || [])
          ];

          // Só chama IA se tiver observação escrita e itens selecionados
          if (dadosFormulario.observacao?.length > 5 && itensAtivos.length > 0) {
              setAnalisandoIA(true); // Ativa spinner do botão
              
              const resultadoIA = await IaService.analisarCoerencia(
                  dadosFormulario.observacao, 
                  dadosFormulario.tipoOcorrencia,
                  itensAtivos
              );
              
              setAnalisandoIA(false); // Desativa spinner

              if (resultadoIA && resultadoIA.inconsistente) {
                  setSugestaoIA(resultadoIA);
                  setJaVerificouIA(true); // Marca que já verificamos
                  setIsLoading(false); // Para o envio
                  
                  // Rola até o alerta
                  setTimeout(() => {
                      const alerta = document.getElementById('box-sugestao-ia');
                      if (alerta) alerta.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 100);
                  
                  return; // <--- IMPEDE O ENVIO AGORA
              }
          }
      } catch (errIA) {
          console.log("IA indisponível, seguindo...", errIA);
          setAnalisandoIA(false);
      }
    }
    // =====================================================================

    // --- MOVIDO PARA O TOPO PARA EVITAR ERRO DE INICIALIZAÇÃO ---
    const itensParaRegistro = dadosFormulario.itens.filter(item => !!item.motivoSelecionado);
    const itensSobra = dadosFormulario.itensSobra || [];

    // ADICIONE ESTAS LINHAS DE DEBUG
    console.log('🔍 DEBUG - dadosFormulario.observacao:', dadosFormulario.observacao);
    console.log('🔍 DEBUG - dadosFormulario completo:', dadosFormulario);

    try {
      // Validação aprimorada: Remove tudo que não for letra (aceita acentos) e checa se tem pelo menos 2 letras
      const nomeApenasLetras = (dadosFormulario.nomeSolicitante || '').replace(/[^a-zA-ZÀ-ÿ]/g, '');
      if (nomeApenasLetras.length < 2) {
        setErro('Por favor, informe um nome válido para o solicitante (pontuações isoladas não são aceitas).');
        setIsLoading(false);
        
        // Rola a tela para cima para o usuário ver o erro
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      
      if (!(dadosFormulario.emailSolicitante || '').trim()) {
        setErro('O email do solicitante é obrigatório.');
        setIsLoading(false);
        return;
      }
      
      if (!(dadosFormulario.telefoneSolicitante || '').trim()) {
        setErro('O telefone do solicitante é obrigatório.');
        setIsLoading(false);
        return;
      }

      // === VALIDAÇÃO DE LOTE OBRIGATÓRIO PARA RECALL ===
      const itensRecallSemLote = itensParaRegistro.filter(item => isRecall(item) && !item.loteRecall?.trim());
      if (itensRecallSemLote.length > 0) {
        setErro('Para itens de Recall, é obrigatório informar o número do Lote.');
        setIsLoading(false);
        return;
      }

      // === VALIDAÇÃO DE TEMPERATURA OBRIGATÓRIA PARA FORA DA TEMPERATURA ADEQUADA ===
      const itensTemperaturaSemValor = itensParaRegistro.filter(item => isForaDaTemperatura(item) && !item.temperatura?.trim());
      if (itensTemperaturaSemValor.length > 0) {
        setErro('Para itens de Fora da Temperatura Adequada, é obrigatório informar a temperatura.');
        setIsLoading(false);
        return;
      }

      if (dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA') {
        const mencionouProdutoNaObs = detectarRelatoIncorreto(dadosFormulario.observacao);
        
        // Verifica se as tabelas estão vazias
        const tabelaItensVazia = dadosFormulario.itens.filter(item => !!item.motivoSelecionado).length === 0;
        const tabelaSobrasVazia = !dadosFormulario.itensSobra || dadosFormulario.itensSobra.length === 0;

        // Se escreveu palavras de "produto" na observação mas não preencheu nenhuma tabela
        if (mencionouProdutoNaObs && tabelaItensVazia && tabelaSobrasVazia) {
            setErro('Detectamos que você descreveu a falta/sobra na observação, mas não adicionou os itens nos campos corretos. Por favor, utilize o botão "+ Adicionar Item" acima.');
            setIsLoading(false);
            
            // Rola a tela para a área de itens de sobra para o cliente ver onde preencher
            window.scrollTo({ top: 500, behavior: 'smooth' });
            return;
        }
      }
  
      // Validação de email básica
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(dadosFormulario.emailSolicitante)) {
        setErro('Por favor, informe um email válido.');
        setIsLoading(false);
        return;
      }

      // Validação para verificar se todos os itens selecionados têm submotivo
      const itensSelecionados = dadosFormulario.itens.filter(item => !!item.motivoSelecionado);
      const itensSemSubmotivo = itensSelecionados.filter(item => !item.submotivoSelecionado);

      if (itensSemSubmotivo.length > 0) {
        setErro('Por favor, selecione o motivo detalhado para todos os itens marcados. Verifique se algum item está com "Selecione o Detalhe".');
        setIsLoading(false);
        return;
      }
      
      // =============================
      // VALIDAÇÃO DOS ITENS DE SOBRA COM EAN
      // =============================
      const itensSobraValidos = itensSobra.filter(item => {
        if (!item.ean || !item.quantidade || !item.motivo) {
          return false;
        }
        if (item.eanValido === false) {
          setErro(`EAN "${item.ean}" não encontrado no sistema. Verifique o código.`);
          return false;
        }
        if (!item.cdMercadoriaEncontrada) {
          setErro(`EAN "${item.ean}" ainda não foi validado. Aguarde a consulta ou digite novamente.`);
          return false;
        }
        return true;
      });

      // // Para SOBRA_MERCADORIA, valida se tem pelo menos um item (da nota OU da sobra)
      // if (dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA') {
      //   if (itensParaRegistro.length === 0 && itensSobraValidos.length === 0) {
      //     setErro('Para sobra de mercadoria, selecione pelo menos um item da nota OU adicione um item válido que sobrou.');
      //     setIsLoading(false);
      //     return;
      //   }
      // } else {
      //   if (itensParaRegistro.length === 0) {
      //     setErro('Selecione um motivo para pelo menos um item.');
      //     setIsLoading(false);
      //     return;
      //   }
      // }

      // ============================================================
      // [NOVO] TRAVA FINAL DE DUPLICIDADE CRUZADA
      // ============================================================
      if (dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA') {
        const codigosSelecionados = new Set();
        
        // 1. Armazena os códigos já checados lá na tabela de cima
        for (const item of itensParaRegistro) {
            codigosSelecionados.add(Number(item.codMercadoria));
        }

        // 2. Verifica se algum EAN digitado embaixo bate com o que está em cima
        for (const itemSobra of itensSobraValidos) {
            if (codigosSelecionados.has(Number(itemSobra.cdMercadoriaEncontrada))) {
                setErro(`O produto "${itemSobra.descricao}" está duplicado. Ele foi marcado na nota fiscal e também adicionado na tabela de sobras simultaneamente.`);
                setIsLoading(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return; // Para o processo na hora
            }
            // Caso o cliente adicione a mesma sobra duas vezes e a API de EAN não tenha pego por algum lag
            codigosSelecionados.add(Number(itemSobra.cdMercadoriaEncontrada)); 
        }
      }
      // ============================================================

      // Verificar se tem submotivo "Outro" e se a observação está vazia
      if (isObservacaoObrigatoria && !dadosFormulario.observacao.trim()) {
        setErro('Quando o motivo "Outros" é selecionado, o campo de observação é obrigatório.');
        setIsLoading(false);
        return;
      }

      // ============================================================
      // [NOVO] VALIDAÇÃO: IMAGEM OBRIGATÓRIA PARA AVARIA
      // ============================================================
      // Se tiver item de avaria qualificada E o array de imagens estiver vazio
      if (temItemAvariaQualificado() && imagens.length === 0) {
        setErro('Para ocorrências de Avaria, é obrigatório anexar pelo menos uma foto como evidência.');
        setIsLoading(false);
        
        // Rola a tela até o campo de upload para o usuário ver onde está o erro
        const uploadElement = document.getElementById('upload-avaria-input');
        if (uploadElement) {
            uploadElement.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Construir dados para API incluindo itensSobra
      const todosItens = [
          // ...itensParaRegistro.map(item => ({
          //     cdMercadoria: item.codMercadoria,
          //     quantidade: item.quantidadeDevolucao,
          //     cdTipoOcorrencia: item.submotivoSelecionado?.cdTipo,
          //     cdSubtipoOcorrencia: item.submotivoSelecionado?.cdSubtipo,
          //     // Observação do item: só para SOLICITACAO (informacaoCorreta), outros tipos = null
          //     dsObservacaoItem: dadosFormulario.tipoOcorrencia === 'SOLICITACAO' 
          //       ? (item.informacaoCorreta || null)
          //       : (dadosFormulario.observacao || null)
          // })),

          ...itensParaRegistro.map(item => {            
            // Monta a observação final (Observação Geral + Protocolo se houver)
            let observacaoFinal = dadosFormulario.observacao || '';
            
            // Validação de Solicitação
            if (dadosFormulario.tipoOcorrencia === 'SOLICITACAO' && item.informacaoCorreta) {
              observacaoFinal = item.informacaoCorreta;
            } 
            
            // Validação de Defeito (Protocolo)
            if (isDefeitoFabrica(item) && item.protocoloIndustria) {
                const prefix = observacaoFinal ? " | " : "";
                observacaoFinal = `${observacaoFinal}${prefix}Protocolo Indústria: ${item.protocoloIndustria}`;
            }

            // Lógica Recall (Validação de Lote)
            if (isRecall(item)) {
              if (!item.loteRecall?.trim()) {
                // Em vez de throw, vamos usar uma mensagem amigável no catch
                throw new Error(`O item ${item.codMercadoria} (Recall) exige a informação do lote.`);
              }
              observacaoFinal = `RECALL - Lote: ${item.loteRecall} | ${observacaoFinal}`;
            }

            // Lógica Fora da Temperatura Adequada (Validação de Temperatura)
            if (isForaDaTemperatura(item)) {
              if (!item.temperatura?.trim()) {
                throw new Error(`O item ${item.codMercadoria} (Fora da Temperatura) exige a informação da temperatura.`);
              }
              observacaoFinal = `TEMPERATURA: ${item.temperatura} | ${observacaoFinal}`;
            }

            // Lógica Ressarcimento (Validação de Valores e Local)
            if (isRessarcimento(item)) {
              if (!item.valorAcordado || !item.valorRecebido || !item.localDesconto) { 
                  throw new Error(`Informe os valores e o local do desconto para o item ${item.codMercadoria}.`); 
              }
              
              // TRAVA DE SEGURANÇA: Garante que a observação base não passe de 100 caracteres
              // para não estourar o limite de 200 do banco ao concatenar o prefixo.
              const obsLimitada = observacaoFinal.substring(0, 100);
              
              observacaoFinal = `RESSARCIMENTO - Local: ${item.localDesconto} | Vl. Acordado: ${item.valorAcordado} | Vl. Recebido: ${item.valorRecebido} | ${obsLimitada}`;
            }

            return {
                cdMercadoria: item.codMercadoria,
                quantidade: item.quantidadeDevolucao,
                cdTipoOcorrencia: item.submotivoSelecionado?.cdTipo,
                cdSubtipoOcorrencia: item.submotivoSelecionado?.cdSubtipo,
                dsObservacaoItem: observacaoFinal || null
            };
            // ============================================================
        }),

          // APENAS ITENS DE SOBRA VÁLIDOS (COM CÓDIGO ENCONTRADO)
          ...itensSobraValidos.map(item => ({
            cdMercadoria: item.cdMercadoriaEncontrada, // Código encontrado pela consulta EAN
            quantidade: parseInt(item.quantidade),
            cdTipoOcorrencia: item.motivo ? parseInt(item.motivo.split('-')[0]) : null,
            cdSubtipoOcorrencia: item.motivo ? parseInt(item.motivo.split('-')[1]) : null,
            dsObservacaoItem: dadosFormulario.observacao || null
          }))
      ];

      const dadosParaAPI = {
        cdCliente: cliente.cdCliente,
        emailContato: dadosFormulario.emailSolicitante || cliente.emails[0],
        nomeContato: dadosFormulario.nomeSolicitante || cliente.razaoSocial,
        telefoneContato: dadosFormulario.telefoneSolicitante || null,
        nfeLst: [{
          cdEmpresa: dadosFormulario.cdEmpresaNota,
          nrNfe: dadosFormulario.nrNfe,
          nrSerieNfe: 1,
          dtEmissao: format(dadosFormulario.dtEmissao, 'dd/MM/yyyy'),
          dsObservacao: dadosFormulario.observacao || null,
          itens: todosItens
        }]
      };

      // ADICIONE ESTA LINHA DE DEBUG
      console.log('🚀 DEBUG - dadosParaAPI antes do envio:', dadosParaAPI);

      const response = await SacService.registrarOcorrencia(dadosParaAPI, imagens);
      if (response.status === 'OK') {
        // 1. Guardamos o número da nota antes de limpar o formulário
        const notaParaFiltrar = dadosFormulario.nrNfe;

        alert(
            'Solicitação enviada para a fila de processamento!\n\n' +
            'Sua ocorrência será integrada em até 3 minutos.\n\n' +
            'O número de protocolo seguirá por e-mail.'
        );

        // 2. Limpa o formulário (seu código atual)
        setDadosFormulario({ nrNfe: '', dtEmissao: null, itens: [], itensSobra: [], nomeSolicitante: '', emailSolicitante: '', observacao: '', tipoOcorrencia: tipoOcorrenciaInicial });
        
        // 3. Redireciona passando o estado da nota
        navigate('/consulta-sac', { state: { nrNfe: notaParaFiltrar } });
        
        setImagens([]);
        setMostrarSelecaoTodos(false);
        setSubmotivoTodos(null);
        setInformacaoCorretaTodos('');
        setPaginaAtual(1);
        setValorDataTexto('');
      } else {
        setErro(response.mensagem);
      }
    } catch (err) {
      console.error('❌ Erro detectado no frontend:', err);
      setErro('Erro ao se comunicar com o servidor.');
    } finally {
      setIsLoading(false);
    }
  };

  // A sua API de consulta de notas fiscais retorna um objeto único na propriedade `conteudo`.
  // Se o nrNfe for informado, a busca por nota fiscal será executada.
  // Filtro para o Select de subtipos
  const subtiposOcorrenciaOptions = tiposOcorrencia[dadosFormulario.tipoOcorrencia] || [];

  return (
    <div className="dashboard-painel boletos-painel">
      {/* BLOCO DO CABEÇALHO COM BOTÃO */}
      <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '10px' 
      }}>
          <div>
              <h3 className="titulo" style={{ marginBottom: '5px' }}>Registro de Ocorrência SAC</h3>
              <p className="texto" style={{ margin: 0 }}>Preencha os dados da nota fiscal para registrar uma ocorrência.</p>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <a 
                href="https://drive.google.com/uc?export=download&id=1Q0TbNa_-aHl_5awwIsHUyjbxWG8E-B2Z" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-manual-pdf"
                title="Ver Política de Devolução"
            >
                <FaFilePdf size={16} />
                <span>Política de Devolução</span>
            </a>

            <a 
                href="https://drive.google.com/uc?export=download&id=1RvwGs3H7vWVt-V9ZsceIsz-1aDsIUHyV" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-manual-pdf"
                title="Baixar Manual em PDF"
            >
                <FaFilePdf size={16} />
                <span>Manual do Portal</span>
            </a>
        </div>
      </div>
      {/* FIM DO BLOCO DO CABEÇALHO */}
      <p className="texto">Motivo selecionado: <strong>{getTipoOcorrenciaDisplay(dadosFormulario.tipoOcorrencia)}</strong></p>

      <p style={{ 
          color: '#dc3545', 
          fontSize: '12px', 
          marginTop: '-10px', 
          marginBottom: '20px',
          fontStyle: 'italic',
          maxWidth: '800px'
      }}>
          <FaInfoCircle size={10} style={{ marginRight: '5px' }} />
          {getResumoOcorrencia(dadosFormulario.tipoOcorrencia)}
      </p>

      {erro && <p style={{ color: 'red' }}>{erro}</p>}
      
      <form onSubmit={handleSubmit}>
        <div className="boletos-filtros">
          <input
            type="text"
            name="nrNfe"
            placeholder="Número da Nota Fiscal"
            value={dadosFormulario.nrNfe}
            onChange={handleInputChange}
            className="input-filtro"
            disabled={dadosFormulario.itens.length > 0}
          />
          <DatePicker
            selected={dadosFormulario.dtEmissao}
            onChange={handleDateChange}
            dateFormat="dd/MM/yyyy"
            locale={ptBR}
            placeholderText="Data de Emissão (DD/MM/AAAA)"
            className="input-filtro"
            disabled={dadosFormulario.itens.length > 0} // Bloqueia se já buscou a nota
            onChangeRaw={(e) => e.preventDefault()}    // IMPEDE A DIGITAÇÃO MANUAL DA DATA DE EMISSÃO
          />
          <button
            type="button"
            className="download-btn"
            onClick={handleConsultarNota}
            disabled={isLoading || !dadosFormulario.nrNfe || !dadosFormulario.dtEmissao}
          >
            {isLoading ? 'Buscando...' : 'Consultar Nota'}
          </button>
        </div>

        {dadosFormulario.itens.length > 0 && (
          <>
            <p style={{ marginBottom: '10px' }}>
              Selecione os itens para {dadosFormulario.tipoOcorrencia === 'DEVOLUCAO' ? 'a devolução' : 
                                      dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA' ? 'reportar falta/sobra' : 
                                      'a solicitação'}:
            </p>

            {mostrarSelecaoTodos && (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                marginBottom: '15px',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Selecionar motivo para todos os itens:</h4>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1', minWidth: '200px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Motivo:</label>
                    <select
                      value={submotivoTodos ? `${submotivoTodos.cdTipo}-${submotivoTodos.cdSubtipo}` : ''}
                      onChange={(e) => {
                        if (!e.target.value) {
                          setSubmotivoTodos(null);
                          return;
                        }
                        const [cdTipo, cdSubtipo] = e.target.value.split('-').map(Number);
                        const selectedOption = subtiposOcorrenciaOptions.find(opt =>
                          opt.cdTipo === cdTipo && opt.cdSubtipo === cdSubtipo
                        );
                        setSubmotivoTodos(selectedOption || null);
                      }}
                      className="input-filtro"
                    >
                      <option value="">Selecione o motivo</option>
                      {subtiposOcorrenciaOptions.map(subtipo => (
                        <option key={`${subtipo.cdTipo}-${subtipo.cdSubtipo}`} value={`${subtipo.cdTipo}-${subtipo.cdSubtipo}`}>
                          {subtipo.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {dadosFormulario.tipoOcorrencia === 'SOLICITACAO' && (
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Informação Correta:</label>
                      <input
                        type="text"
                        value={informacaoCorretaTodos || ''}
                        onChange={(e) => setInformacaoCorretaTodos(e.target.value)}
                        placeholder="Ex: Lote correto: L123456"
                        className="input-filtro"
                      />
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      className="download-btn btn-aplicar-todos"
                      onClick={handleConfirmarSelecaoTodos}
                    >
                      Aplicar a Todos
                    </button>
                    <button
                      type="button"
                      className="download-btn btn-cancelar"
                      onClick={handleCancelarSelecaoTodos}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="boletos-lista" style={{ overflow: 'visible' }}>
              <table className="boletos-tabela">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={dadosFormulario.itens.length > 0 && dadosFormulario.itens.every(item => !!item.motivoSelecionado)}
                      ref={checkboxRef => {
                        if (checkboxRef) {
                          const algunsSelecionados = dadosFormulario.itens.some(item => !!item.motivoSelecionado);
                          const todosSelecionados = dadosFormulario.itens.every(item => !!item.motivoSelecionado);
                          checkboxRef.indeterminate = algunsSelecionados && !todosSelecionados;
                        }
                      }}
                      onChange={handleSelectAllItems}
                      title="Selecionar/Desselecionar todos"
                    />
                  </th>
                  <th>Código Mercadoria</th>
                  <th>Descrição</th>
                  <th>Qtd. Original</th>
                  <th>{getQuantidadeLabel(dadosFormulario.tipoOcorrencia)}</th>
                  <th>Motivo</th>

                  {/* Renderização condicional rigorosa das colunas extras */}
                  {exibirColunaInformacaoCorreta && <th>Informação Correta</th>}
                  {exibirColunaRessarcimento && (
                    <>
                      <th>Local do Desconto</th>
                      <th>Vl. Acordado</th>
                      <th>Vl. Recebido</th>
                    </>
                  )}
                  {exibirColunaProtocolo && <th>Protocolo Indústria</th>}
                  {exibirColunaRecall && <th>Lote do Recall</th>}
                  {exibirColunaTemperatura && <th>Temperatura (°C)</th>}
                </tr>
              </thead>
              <tbody>
                {calcularItensExibidos().map((item, index) => {
                  const indiceOriginal = (paginaAtual - 1) * itensPorPagina + index;

                  // [ADICIONE AQUI] Lógica de bloqueio
                  const itemBloqueado = deveBloquearItem(item);

                  return (
                    <tr 
                      key={indiceOriginal}
                      style={{ 
                          backgroundColor: itemBloqueado ? '#f9f9f9' : 'inherit',
                          opacity: itemBloqueado ? 0.6 : 1 
                      }}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={!!item.motivoSelecionado}
                          onChange={() => handleItemSelect(index)}
                          disabled={itemBloqueado} // [ADICIONE AQUI] Bloqueio funcional do checkbox
                        />
                      </td>
                      <td>
                        {/* [SUBSTITUA A CÉLULA DO CÓDIGO POR ISTO] */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {item.codMercadoria}
                            
                            {itemBloqueado && (
                                <div title="Este produto é termolábil. Devolução permitida apenas no ato da entrega.">
                                    <FaInfoCircle color="#dc3545" style={{ cursor: 'help' }} />
                                </div>
                            )}
                        </div>
                      </td>
                      {/* <td>{item.codMercadoria}</td> */}
                      <td>{item.nmMercadoria}</td>
                      <td>{item.quantidade}</td>
                      <td>
                      <input
                        type="number"
                        value={item.quantidadeDevolucao}
                        onChange={e => handleQuantidadeDevolucao(e, index)}
                        className="input-filtro"
                        min="1"
                        max={item.quantidade}
                        disabled={!item.motivoSelecionado}
                        placeholder={getQuantidadePlaceholder(dadosFormulario.tipoOcorrencia)}
                      />
                      </td>
                      <td>
                        <select
                            value={item.submotivoSelecionado ? `${item.submotivoSelecionado.cdTipo}-${item.submotivoSelecionado.cdSubtipo}` : ''}
                            onChange={(e) => {
                                if (!e.target.value) {
                                    handleSubmotivoChange(null, index);
                                    return;
                                }
                                const [cdTipo, cdSubtipo] = e.target.value.split('-').map(Number);
                                const selectedOption = subtiposOcorrenciaOptions.find(opt => 
                                    opt.cdTipo === cdTipo && opt.cdSubtipo === cdSubtipo
                                );
                                handleSubmotivoChange(selectedOption || null, index);
                            }}
                            disabled={!item.motivoSelecionado}
                            className="input-filtro"
                        >
                            <option value="">Selecione o Detalhe</option>
                            {subtiposOcorrenciaOptions.map(subtipo => (
                                <option key={`${subtipo.cdTipo}-${subtipo.cdSubtipo}`} value={`${subtipo.cdTipo}-${subtipo.cdSubtipo}`}>
                                {subtipo.label}
                                </option>
                            ))}
                        </select>
                      </td>

                      {/* 1. COLUNA INFORMAÇÃO CORRETA */}
                      {exibirColunaInformacaoCorreta && (
                        <td>
                          {isCorrecao(item) ? (
                            <input
                              type="text"
                              value={item.informacaoCorreta || ''}
                              onChange={e => handleInformacaoCorretaChange(e, indiceOriginal)}
                              placeholder="Digite o dado correto"
                              className="input-filtro"
                            />
                          ) : <span style={{ color: '#ccc' }}>-</span>}
                        </td>
                      )}

                      {/* 2. COLUNAS DE REEMBOLSO (VALORES SEPARADOS) */}
                      {exibirColunaRessarcimento && (
                        <>
                          <td>
                            {isRessarcimento(item) ? (
                                <select
                                  value={item.localDesconto || ''}
                                  onChange={(e) => {
                                    const novosItens = [...dadosFormulario.itens];
                                    novosItens[indiceOriginal].localDesconto = e.target.value;
                                    setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
                                  }}
                                  className="input-filtro"
                                  style={{ 
                                    width: '130px', 
                                    border: (item.motivoSelecionado && isRessarcimento(item) && !item.localDesconto) ? '2px solid #dc3545' : '1px solid #dee2e6' 
                                  }}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="E-GAM">E-GAM</option>
                                  <option value="TELEVENDAS">TELEVENDAS</option>
                                  <option value="OUTROS">OUTROS</option>
                                </select>
                            ) : <span style={{ color: '#ccc' }}>-</span>}
                          </td>
                          <td>
                            {isRessarcimento(item) ? (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="0,00"
                                className="input-filtro"
                                style={{ width: '100px' }}
                                value={item.valorAcordado || ''}
                                onChange={(e) => {
                                  const novosItens = [...dadosFormulario.itens];
                                  novosItens[indiceOriginal].valorAcordado = e.target.value;
                                  setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
                                }}
                              />
                            ) : <span style={{ color: '#ccc' }}>-</span>}
                          </td>
                          <td>
                            {isRessarcimento(item) ? (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="0,00"
                                className="input-filtro"
                                style={{ width: '100px' }}
                                value={item.valorRecebido || ''}
                                onChange={(e) => {
                                  const novosItens = [...dadosFormulario.itens];
                                  novosItens[indiceOriginal].valorRecebido = e.target.value;
                                  setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
                                }}
                              />
                            ) : <span style={{ color: '#ccc' }}>-</span>}
                          </td>
                        </>
                      )}

                      {/* 3. COLUNA PROTOCOLO */}
                      {exibirColunaProtocolo && (
                        <td>
                          {isDefeitoFabrica(item) ? (
                            <input
                              type="text"
                              value={item.protocoloIndustria || ''}
                              onChange={e => handleProtocoloIndustriaChange(e, index)}
                              placeholder="Protocolo"
                              className="input-filtro"
                            />
                          ) : <span>-</span>}
                        </td>
                      )}

                      {/* 4. COLUNA RECALL */}
                      {exibirColunaRecall && (
                        <td>
                          {isRecall(item) ? (
                            <input
                              type="text"
                              value={item.loteRecall || ''}
                              onChange={e => {
                                const novosItens = [...dadosFormulario.itens];
                                novosItens[indiceOriginal].loteRecall = e.target.value;
                                setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
                              }}
                              placeholder="Informe o Lote"
                              className="input-filtro"
                              // Adiciona borda vermelha se estiver vazio e for Recall
                              style={{ 
                                border: (item.motivoSelecionado && isRecall(item) && !item.loteRecall?.trim()) 
                                        ? '2px solid #dc3545' 
                                        : '1px solid #dee2e6' 
                              }}
                            />
                          ) : <span>-</span>}
                        </td>
                      )}

                      {/* 5. COLUNA TEMPERATURA */}
                      {exibirColunaTemperatura && (
                        <td>
                          {isForaDaTemperatura(item) ? (
                            <input
                              type="text"
                              value={item.temperatura || ''}
                              onChange={e => {
                                const novosItens = [...dadosFormulario.itens];
                                novosItens[indiceOriginal].temperatura = e.target.value;
                                setDadosFormulario(prev => ({ ...prev, itens: novosItens }));
                              }}
                              placeholder="Ex: 8°C"
                              className="input-filtro"
                              style={{
                                border: (item.motivoSelecionado && isForaDaTemperatura(item) && !item.temperatura?.trim())
                                        ? '2px solid #dc3545'
                                        : '1px solid #dee2e6'
                              }}
                            />
                          ) : <span>-</span>}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* PAGINAÇÃO */}
              {calcularTotalPaginas() > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  marginTop: '20px',
                  gap: '5px'
                }}>
                  <button
                    onClick={paginaAnterior}
                    disabled={paginaAtual === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #dee2e6',
                      backgroundColor: paginaAtual === 1 ? '#f8f9fa' : 'white',
                      color: paginaAtual === 1 ? '#6c757d' : '#007bff',
                      cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    ‹
                  </button>
                  
                  {Array.from({ length: calcularTotalPaginas() }, (_, i) => i + 1).map(numeroPagina => (
                    <button
                      key={numeroPagina}
                      onClick={() => irParaPagina(numeroPagina)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #dee2e6',
                        backgroundColor: numeroPagina === paginaAtual ? '#007bff' : 'white',
                        color: numeroPagina === paginaAtual ? 'white' : '#007bff',
                        cursor: 'pointer',
                        borderRadius: '4px'
                      }}
                    >
                      {numeroPagina}
                    </button>
                  ))}
                  
                  <button
                    onClick={proximaPagina}
                    disabled={paginaAtual === calcularTotalPaginas()}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #dee2e6',
                      backgroundColor: paginaAtual === calcularTotalPaginas() ? '#f8f9fa' : 'white',
                      color: paginaAtual === calcularTotalPaginas() ? '#6c757d' : '#007bff',
                      cursor: paginaAtual === calcularTotalPaginas() ? 'not-allowed' : 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>

            {dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA' && (
              <div style={{ marginTop: '20px', backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0 }}>Produtos que Sobraram e/ou Faltaram (não constam na nota fiscal)</h4>
                  <button 
                    type="button" 
                    className="download-btn btn-aplicar-todos"
                    onClick={adicionarItemSobra}
                    // style={{ backgroundColor: '#28a745' }}
                  >
                    + Adicionar Item
                  </button>
                </div>
                
                {/* {dadosFormulario.itensSobra && dadosFormulario.itensSobra.length > 0 && (
                  <div className="boletos-lista" style={{ overflow: 'visible' }}>
                    <table className="boletos-tabela">
                      <thead>
                        <tr>
                          <th>EAN/Código de Barras</th>
                          <th>Descrição do Produto</th>
                          <th>Quantidade</th>
                          <th>Motivo</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dadosFormulario.itensSobra.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="text"
                                  placeholder="EAN (código de barras)"
                                  value={item.ean || ''}
                                  onChange={e => handleItemSobraChange(index, 'ean', e.target.value)}
                                  className="input-filtro"
                                  style={{
                                    borderColor: item.eanValido === false ? '#dc3545' :
                                                item.eanValido === true ? '#28a745' : '#dee2e6'
                                  }}
                                />
                                {loadingEAN[index] && (
                                  <div style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#007bff'
                                  }}>
                                    ⏳
                                  </div>
                                )}
                              </div>
                              {item.eanValido === false && (
                                <small style={{ color: '#dc3545' }}>EAN não encontrado</small>
                              )}
                              {item.eanValido === true && (
                                <small style={{ color: '#28a745' }}>✓ EAN válido</small>
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                placeholder="Ex: Dipirona 500mg 10cp"
                                value={item.descricao || ''}
                                onChange={e => handleItemSobraChange(index, 'descricao', e.target.value)}
                                className="input-filtro"
                                style={{ minWidth: '200px' }}
                                disabled={loadingEAN[index]} // Desabilita enquando busca EAN
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                placeholder="Qtd"
                                value={item.quantidade || ''}
                                onChange={e => handleItemSobraChange(index, 'quantidade', e.target.value)}
                                className="input-filtro"
                                min="1"
                              />
                            </td>
                            <td>
                              <select
                                value={item.motivo || ''}
                                onChange={e => handleItemSobraChange(index, 'motivo', e.target.value)}
                                className="input-filtro"
                              >
                                <option value="">Selecione o motivo</option>
                                <option value="20-2">MERCADORIA ENVIADA EM EXCESSO</option>
                                <option value="20-3">MERCADORIA NÃO PERTENCE A NF</option>
                              </select>
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removerItemSobra(index)}
                                style={{ 
                                  backgroundColor: '#dc3545', 
                                  color: 'white', 
                                  border: 'none', 
                                  padding: '5px 10px', 
                                  borderRadius: '4px' 
                                }}
                              >
                                Remover
                              </button>
                            </td>
                          </tr>
                        ))}
                        </tbody>
                    </table>
                  </div>
                )} */}

                {dadosFormulario.itensSobra && dadosFormulario.itensSobra.length > 0 && (
                    <div className="boletos-lista" style={{ overflow: 'visible' }}>
                        <table className="boletos-tabela">
                            <thead>
                                <tr>
                                    <th>EAN/Código de Barras</th>
                                    <th>Descrição do Produto</th>
                                    <th>Quantidade</th>
                                    <th>Motivo</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dadosFormulario.itensSobra.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div style={{ position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    placeholder="EAN (código de barras)"
                                                    value={item.ean || ''}
                                                    onChange={e => handleItemSobraChange(index, 'ean', e.target.value)}
                                                    className="input-filtro"
                                                    style={{
                                                        borderColor: item.eanValido === false ? '#dc3545' :
                                                                  item.eanValido === true ? '#28a745' : '#dee2e6'
                                                    }}
                                                />
                                                {loadingEAN[index] && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        right: '10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '5px'
                                                    }}>
                                                        <div style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            border: '2px solid #f3f3f3',
                                                            borderTop: '2px solid #007bff',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }}></div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* {item.eanValido === false && (
                                                <small style={{ color: '#dc3545' }}>EAN não encontrado</small>
                                            )} */}
                                            {item.eanValido === false && (
                                                <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontWeight: 'bold' }}>
                                                    {item.erroEAN || 'EAN não encontrado'}
                                                </small>
                                            )}
                                            {item.eanValido === true && (
                                                <small style={{ color: '#28a745' }}>✓ EAN válido</small>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                placeholder={loadingEAN[index] ? "Aguarde, carregando..." : "Ex: Dipirona 500mg 10cp"}
                                                value={item.descricao || ''}
                                                onChange={e => handleItemSobraChange(index, 'descricao', e.target.value)}
                                                className="input-filtro"
                                                style={{ 
                                                    minWidth: '200px',
                                                    fontStyle: loadingEAN[index] ? 'italic' : 'normal',
                                                    color: loadingEAN[index] ? '#6c757d' : 'inherit'
                                                }}
                                                disabled={loadingEAN[index]}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                placeholder="Qtd"
                                                value={item.quantidade || ''}
                                                onChange={e => handleItemSobraChange(index, 'quantidade', e.target.value)}
                                                className="input-filtro"
                                                min="1"
                                                disabled={loadingEAN[index]}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                value={item.motivo || ''}
                                                onChange={e => handleItemSobraChange(index, 'motivo', e.target.value)}
                                                className="input-filtro"
                                                disabled={loadingEAN[index]}
                                            >
                                                <option value="">Selecione o motivo</option>
                                                <option value="4-6">FALTA DE MERCADORIA</option>
                                                <option value="20-2">MERCADORIA ENVIADA EM EXCESSO</option>
                                                <option value="20-3">MERCADORIA NÃO PERTENCE A NF</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => removerItemSobra(index)}
                                                disabled={loadingEAN[index]}
                                                className='btn-remover'
                                            >
                                                Remover
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
              </div>
            )}
            
            <div style={{ marginTop: '20px' }}>
              <h4 className="titulo">Detalhes do Solicitante e Observações</h4>
              <div className="boletos-filtros">
                <input
                  type="text"
                  name="nomeSolicitante"
                  placeholder="Nome do Solicitante"
                  value={dadosFormulario.nomeSolicitante}
                  onChange={handleInputChange}
                  className="input-filtro"
                />
                <input
                  type="email"
                  name="emailSolicitante"
                  placeholder="E-mail do Solicitante"
                  value={dadosFormulario.emailSolicitante}
                  onChange={handleInputChange}
                  className="input-filtro"
                />
                <input
                  type="text"
                  name="telefoneSolicitante"
                  placeholder="Whatsapp do Solicitante"
                  value={dadosFormulario.telefoneSolicitante}
                  onChange={handleTelefoneChange}
                  className="input-filtro"
                  maxLength="15"
                />
              </div>

              {/* 2. A MENSAGEM DE ERRO FICA AQUI FORA, PARA NÃO QUEBRAR O SEU CSS */}
              {(dadosFormulario.nomeSolicitante && !isNomeValido(dadosFormulario.nomeSolicitante)) && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '-10px', marginBottom: '15px', marginLeft: '5px' }}>
                  ⚠️ Informe um nome válido (apenas letras, sem pontuações).
                </div>
              )}

              {dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA' && (
                  <div style={{
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '10px',
                      fontSize: '13px',
                      border: '1px solid #ffeaa7',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      lineHeight: '1.4'
                  }}>
                      <FaInfoCircle size={20} />
                      <span>
                          <strong>Aviso Importante:</strong> Se houve falta ou sobra de produtos (que não estejam presentes na nota fiscal), 
                          você deve <strong>obrigatoriamente</strong> utilizar o botão 
                          <span style={{ color: '#007bff' }}> "+ Adicionar Item" </span> 
                          acima para informar os códigos e quantidades. Ou então informar o item presente na nota fiscal e a quantidade que está faltando/sobrando.
                      </span>
                  </div>
              )}

              {detectarRelatoIncorreto(dadosFormulario.observacao) && dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA' && (
                  <p style={{ color: '#dc3545', fontSize: '12px', fontWeight: 'bold', margin: '5px 0' }}>
                      ⚠️ Atenção: Use os campos de "Produtos que Sobraram/Faltaram" acima para listar as mercadorias.
                  </p>
              )}

              <textarea
                name="observacao"
                placeholder={
                  temLocalDescontoOutros() ? "Descreva o local que estava aplicado o desconto no momento da compra..." :
                  temSubmotivoOutro() ? "Observações adicionais... (OBRIGATÓRIO quando o motivo 'Outros' é selecionado)" : 
                  "Observações adicionais..."
                }
                value={dadosFormulario.observacao}
                onChange={handleInputChange}
                // LIMITE DINÂMICO: 100 se for reembolso, 200 para os demais
                maxLength={exibirColunaRessarcimento ? 100 : 200}
                style={{ 
                    width: '100%', 
                    height: '100px', 
                    padding: '10px', 
                    marginTop: '10px', 
                    borderRadius: '8px', 
                    border: isObservacaoObrigatoria ? '2px solid #ff6b6b' : '1px solid #dee2e6',
                    backgroundColor: isObservacaoObrigatoria ? '#fff5f5' : 'white'
                  }}
                  required={isObservacaoObrigatoria}
              />
              <div style={{ 
                  textAlign: 'right', 
                  fontSize: '12px', 
                  // COR DINÂMICA: Fica vermelho se chegar perto do limite atual
                  color: dadosFormulario.observacao.length > (exibirColunaRessarcimento ? 80 : 180) ? '#ff6b6b' : '#6c757d',
                  marginTop: '5px' 
              }}>
                  {dadosFormulario.observacao.length}/{exibirColunaRessarcimento ? 100 : 200} caracteres
              </div>
              {isObservacaoObrigatoria && (
                <p style={{ color: '#ff6b6b', fontSize: '12px', margin: '5px 0 0 0', fontWeight: 'bold' }}>
                  * Campo obrigatório: Você selecionou a opção "OUTROS". Por favor, detalhe acima.
                </p>
              )}
            </div>

            {/* FEEDBACK DA IA */}
            {analisandoIA && (
                <div style={{ fontSize: '12px', color: '#007bff', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div className="spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></div>
                    O assistente virtual está analisando sua solicitação...
                </div>
            )}

            {/* FEEDBACK DA IA - VERSÃO AMIGÁVEL */}
            {sugestaoIA && (
              <div id="box-sugestao-ia" style={{ 
                  backgroundColor: '#fff3cd', 
                  borderLeft: '5px solid #ffc107',
                  color: '#856404', 
                  padding: '20px', 
                  borderRadius: '4px', 
                  marginTop: '25px', 
                  marginBottom: '25px',
                  display: 'flex',
                  gap: '15px',
                  animation: 'fadeIn 0.5s'
              }}>
                  <div style={{ color: '#d39e00', fontSize: '24px' }}>
                      <FaRobot />
                  </div>
                  <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>
                          Sugestão do Assistente Virtual
                      </h4>
                      
                      {/* <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                          {sugestaoIA.mensagemCurta}
                      </p> */}

                      {/* LISTA DE ITENS INCORRETOS */}
                      {sugestaoIA.itensSuspeitos && sugestaoIA.itensSuspeitos.length > 0 && (
                          <div style={{ marginBottom: '15px' }}>
                              <strong>Prezado cliente, os itens abaixo parecem estar na categoria incorreta:</strong>
                              <ul style={{ margin: '5px 0 10px 20px', fontSize: '13px', color: '#856404' }}>
                                  {sugestaoIA.itensSuspeitos.map((nome, idx) => (
                                      <li key={idx}>Item: <b>{nome}</b></li>
                                  ))}
                              </ul>
                          </div>
                      )}

                      {/* SUGESTÃO DE CAMINHO COM DESTAQUE */}
                      {sugestaoIA.caminhoCorretoCodigo && MAPA_CAMINHOS[sugestaoIA.caminhoCorretoCodigo] && (
                          <div style={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.7)', 
                              padding: '10px', 
                              borderRadius: '4px',
                              border: '1px dashed #d39e00',
                              marginBottom: '15px'
                          }}>
                              <span>Para registrar estes itens corretamente, acesse:</span>
                              <br/>
                              <strong style={{ fontSize: '15px', color: '#d39e00', display: 'block', marginTop: '5px' }}>
                                  {/* AQUI ESTÁ A MÁGICA: Concatena o motivo específico se a IA mandou */}
                                  {MAPA_CAMINHOS[sugestaoIA.caminhoCorretoCodigo]}
                                  {sugestaoIA.motivoEspecificoSugestao ? ` -> ${sugestaoIA.motivoEspecificoSugestao}` : ''}
                              </strong>
                          </div>
                      )}
                  </div>
              </div>
          )}

            {/* ======================================================== */}
            {/* BLOCO DE UPLOAD DE IMAGENS - SÓ APARECE SE FOR AVARIA */}
            {/* ======================================================== */}
            {temItemAvariaQualificado() && (
              <div style={{ 
                marginTop: '15px', 
                marginBottom: '20px', // <--- ADICIONE ESTA LINHA AQUI
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6' 
                }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaCamera /> Evidências de Avaria (Obrigatório)
                  </h4>
                  <p style={{ fontSize: '13px', margin: '0 0 10px 0', color: '#6c757d' }}>
                      Adicione até 2 fotos demonstrando a avaria (Máx 1 MB cada).
                  </p>
                  
                  <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                      id="upload-avaria-input"
                      disabled={imagens.length >= 2}
                  />
                  
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <label 
                          htmlFor="upload-avaria-input" 
                          className="download-btn"
                          style={{ 
                              backgroundColor: imagens.length >= 2 ? '#ccc' : undefined,
                              cursor: imagens.length >= 2 ? 'not-allowed' : 'pointer',
                              margin: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '14px',
                              padding: '8px 15px'
                        }}
                      >
                          <FaCamera /> Adicionar Foto
                      </label>

                      {imagens.map((img, idx) => (
                          <div key={idx} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '5px', 
                              backgroundColor: 'white', 
                              padding: '5px 10px', 
                              borderRadius: '4px',
                              border: '1px solid #dee2e6'
                          }}>
                              <span style={{ fontSize: '12px', maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {img.name}
                              </span>
                              <FaTrash 
                                  size={12}
                                  style={{ color: '#dc3545', cursor: 'pointer', marginLeft: '5px' }} 
                                  onClick={() => removerImagem(idx)}
                                  title="Remover imagem"
                              />
                          </div>
                      ))}
                  </div>
              </div>
            )}
            {/* ======================================================== */}

            {(() => {
              const itensSelecionados = dadosFormulario.itens.filter(item => !!item.motivoSelecionado);
              const itensSemSubmotivo = itensSelecionados.filter(item => !item.submotivoSelecionado);
              
              if (itensSemSubmotivo.length > 0) {
                return (
                  <div style={{ 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '4px', 
                    padding: '10px', 
                    margin: '15px 0',
                    color: '#856404'
                  }}>
                    <strong>⚠️ Atenção:</strong> Você selecionou {itensSemSubmotivo.length} item(ns) mas não escolheu o motivo detalhado. 
                    Por favor, selecione uma opção no campo "Motivo" para todos os itens marcados.
                  </div>
                );
              }
              return null;
            })()}

            {/* 2. ALERTA VERMELHO (Fotos) - NOVO */}
            {temItemAvariaQualificado() && imagens.length === 0 && (
                <div style={{ 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb', 
                  borderRadius: '4px', 
                  padding: '10px', 
                  margin: '15px 0', 
                  color: '#721c24'
                }}>
                  <strong>⚠️ Foto Obrigatória:</strong> Para registrar ocorrências de Avaria, é obrigatório anexar pelo menos uma foto como evidência no campo acima.
                </div>
            )}

            {/* 3. ALERTA VERMELHO (Lote Obrigatório) - NOVO */}
            {dadosFormulario.itens.some(item => item.motivoSelecionado && isRecall(item) && !item.loteRecall?.trim()) && (
                <div style={{ 
                    backgroundColor: '#f8d7da', 
                    border: '1px solid #f5c6cb', 
                    borderRadius: '4px', 
                    padding: '10px', 
                    margin: '15px 0', 
                    color: '#721c24'
                }}>
                    <strong>⚠️ Lote Obrigatório:</strong> Você selecionou um item de Recall. Por favor, informe o número do <strong>Lote</strong> na tabela acima para prosseguir.
                </div>
            )}

            {/* 4. ALERTA VERMELHO (Temperatura Obrigatória) - NOVO */}
            {dadosFormulario.itens.some(item => item.motivoSelecionado && isForaDaTemperatura(item) && !item.temperatura?.trim()) && (
                <div style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    padding: '10px',
                    margin: '15px 0',
                    color: '#721c24'
                }}>
                    <strong>⚠️ Temperatura Obrigatória:</strong> Você selecionou um item Fora da Temperatura Adequada. Por favor, informe a <strong>Temperatura</strong> na tabela acima para prosseguir.
                </div>
            )}

            <button
              type="submit" 
              className="download-btn" 
              disabled={
                isLoading || 
                analisandoIA ||
                (dadosFormulario.nomeSolicitante || '').replace(/[^a-zA-ZÀ-ÿ]/g, '').length < 2 ||
                !(dadosFormulario.emailSolicitante || '').trim() ||
                !(dadosFormulario.telefoneSolicitante || '').trim() ||
                (dadosFormulario.tipoOcorrencia === 'SOBRA_MERCADORIA' 
                  ? (dadosFormulario.itens.filter(item => item.motivoSelecionado).length === 0 && 
                    (!dadosFormulario.itensSobra || dadosFormulario.itensSobra.length === 0))
                  : dadosFormulario.itens.filter(item => item.motivoSelecionado).length === 0
                ) ||
                  // ====== NOVA VALIDAÇÃO ADICIONADA AQUI ======
                  dadosFormulario.itens.some(item => item.motivoSelecionado && !item.submotivoSelecionado) ||
                  // ====== FIM DA NOVA VALIDAÇÃO ======

                  // ====== [NOVO] VALIDAÇÃO DO BOTÃO ======
                  // Bloqueia se for avaria qualificada E não tiver imagens
                  (temItemAvariaQualificado() && imagens.length === 0) ||
                  // === NOVA TRAVA: Botão apaga se faltar lote no Recall ===
                  (dadosFormulario.itens.some(item => item.motivoSelecionado && isRecall(item) && !item.loteRecall?.trim())) ||
                  // === NOVA TRAVA: Bloqueia se faltar o Local do Desconto no Reembolso ===
                  (dadosFormulario.itens.some(item => item.motivoSelecionado && isRessarcimento(item) && !item.localDesconto)) ||
                  // === NOVA TRAVA: Botão apaga se faltar temperatura em Fora da Temperatura Adequada ===
                  (dadosFormulario.itens.some(item => item.motivoSelecionado && isForaDaTemperatura(item) && !item.temperatura?.trim()))
                  // =====================================
              }
              style={{ marginTop: sugestaoIA ? '0px' : '20px' }} // Ajuste fino opcional
            >
              {/* {isLoading ? 'Enviando...' : analisandoIA ? 'Analisando...' : 'Registrar Ocorrência'} */}
              {/* Muda o texto dinamicamente */}
              {analisandoIA 
                ? <span><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{marginRight: '5px'}}></span>Analisando solicitação...</span> 
                : isLoading 
                    ? 'Enviando...' 
                    : sugestaoIA 
                        ? 'Ignorar aviso e Registrar' // Texto muda se o aviso estiver na tela
                        : 'Registrar Ocorrência'
              }
            </button>
          </>
        )}
      </form>
    </div>
  );
}

export default RegistroSAC;